import { isWide } from '@/lib/placeCatalogue'

/**
 * The words and numbers of Brian's onboarding v2.12 (after/onboarding.html, Oct 30 to 31): the greeting carousel, Emma's
 * hello, the creator classes, the positions, the mix tour's captions, Emma's lines. Topic ids are ours
 * (packages/shared taxonomy); Brian's short ids are mapped once, here.
 */

// ---------------- Howdy: your language (v2.12, a US launch in English and Spanish) ----------------

export type UiLang = 'en' | 'es'
export type LangPick = UiLang | 'both'

/** The three ways in, each named in the other language too (PICKS). Bilingual: the app speaks English, the scripts come in both. */
export const PICKS: [LangPick, string, string][] = [['en', 'English', 'Inglés'], ['es', 'Español', 'Spanish'], ['both', 'Bilingual', 'Bilingüe']]

/** Farm-country greetings, English and Spanish taking turns, Howdy first (GREETS). */
export const GREETS: [string, 'en-US' | 'es-US'][] = [
  ['Howdy', 'en-US'], ['¡Buenas!', 'es-US'], ['Hey y’all', 'en-US'], ['¿Qué tal?', 'es-US'],
  ['Mornin’', 'en-US'], ['¡Buenos días!', 'es-US'], ['Well, hey there', 'en-US'], ['¿Cómo le va?', 'es-US'],
]
export const LOCALE_NAME: Record<string, string> = { 'en-US': 'English · United States', 'es-US': 'Español · Estados Unidos' }
export const CONTINUE: Record<LangPick, string> = { en: 'Continue', es: 'Continuar', both: 'Continue · Continuar' }
/** The line under the picker, in the language picked. */
export const BOTH_LINE: Record<LangPick, string> = {
  en: 'Post in both? Pick Bilingual.', es: '¿Publicas en los dos? Elige Bilingüe.', both: 'Your scripts come in both. Tus guiones, en los dos.',
}

/** Pre-set from the device: any Spanish → Español, anything else → English. */
export function deviceLang(): UiLang {
  try {
    const tag = (navigator.languages && navigator.languages[0]) || navigator.language || ''
    return tag.toLowerCase().startsWith('es') ? 'es' : 'en'
  } catch { return 'en' }
}

/**
 * Emma's hello, in the language picked (v2.8, Brian: "Howdy neighbor. Let's start growing your reach."). Spanish stays
 * gender-neutral (Brian, Oct 31: "use the neutral, as this is for both male and female users"). Draft Spanish: needs a
 * native US-Spanish read before launch.
 */
export const HELLO = {
  en: { head: 'Howdy neighbor.', lede: 'Let’s start growing your reach. I’m Emma, and I’ll set GoGrov up around you: your town, what you cover, and how you sound. About eight minutes, and I do most of the digging.', btn: 'Let’s start' },
  es: { head: '¡Buenas! Qué gusto conocerte.', lede: 'Vamos a hacer crecer tu alcance. Soy Emma y voy a configurar GoGrov a tu medida: tu ciudad, los temas que tratas y tu forma de hablar. Unos ocho minutos, y la mayor parte del trabajo la hago yo.', btn: 'Empecemos' },
}
export function hello(pick: LangPick) {
  if (pick === 'both') return { head: [HELLO.en.head, HELLO.es.head], lede: `${HELLO.en.lede} Y sí, también hablo español.`, btn: 'Let’s start · Empecemos' }
  const h = HELLO[pick]
  return { head: [h.head], lede: h.lede, btn: h.btn }
}

// ---------------- You ----------------

export const BIZ = ['Real estate', 'Lending', 'Tourism', 'A shop or restaurant', 'Other']
export const PLATS = ['Instagram', 'TikTok', 'YouTube', 'Facebook', 'A newsletter']
export const LAUNCH_LANGS: UiLang[] = ['en', 'es']

// ---------------- Topics: the creator classes ----------------

/** Brian's short topic ids → ours. */
const B: Record<string, string> = {
  realestate: 'real-estate-market', newbusiness: 'new-business-openings', civic: 'civic-government', events: 'events', food: 'food-drink',
  safety: 'safety-alerts', schools: 'schools', development: 'development-construction', roads: 'roads-transit', jobs: 'jobs-hiring',
  homegarden: 'home-garden', outdoor: 'outdoor-recreation', tourism: 'tourism-destination', weather: 'snow-weather', localsports: 'local-sports',
  arts: 'arts-culture', agriculture: 'agriculture-farming', nonprofits: 'nonprofits-causes', health: 'health-wellness', politics: 'politics',
  geopolitics: 'geopolitics', military: 'military', finance: 'finance', technology: 'technology', space: 'space', celebritynews: 'celebrity-news',
  realitytv: 'reality-tv', truecrime: 'true-crime', cars: 'cars', history: 'history-heritage', artistsbands: 'artists-bands', prosports: 'pro-sports', fitness: 'fitness',
}
const stats = (o: Record<string, number>) => Object.fromEntries(Object.entries(o).map(([k, v]) => [B[k], v]))

export interface CreatorClass { id: string; name: string; line: string; stats: Record<string, number> }

/** A class is a character sheet, its stats the topic weights (the July design's ten, redrawn; v2). */
export const CLASSES: CreatorClass[] = [
  { id: 'local', name: 'Local reporter', line: 'What’s happening in town: council, new businesses, what’s being built.', stats: stats({ civic: 25, newbusiness: 20, development: 15, safety: 15, schools: 15, events: 10 }) },
  { id: 'todo', name: 'Things to do', line: 'Weekends, festivals, shows, the trail and the patio.', stats: stats({ events: 30, outdoor: 20, food: 15, arts: 15, tourism: 10, artistsbands: 10 }) },
  { id: 'food', name: 'Food & drink', line: 'Openings, menus, breweries, the line out the door.', stats: stats({ food: 35, newbusiness: 20, events: 20, tourism: 15, arts: 10 }) },
  { id: 'realestate', name: 'Real estate', line: 'Homes, what sold, what’s coming, the neighborhoods.', stats: stats({ realestate: 30, development: 20, newbusiness: 15, schools: 15, civic: 10, finance: 10 }) },
  { id: 'sports', name: 'Sports', line: 'Friday nights, the pros, the gym.', stats: stats({ localsports: 35, prosports: 25, schools: 15, fitness: 15, events: 10 }) },
  { id: 'outdoors', name: 'Outdoors', line: 'Trails, snow, rivers, the weather that decides the weekend.', stats: stats({ outdoor: 35, weather: 20, tourism: 15, events: 15, agriculture: 15 }) },
  { id: 'money', name: 'Business & money', line: 'Who’s hiring, who’s opening, rates and what they mean.', stats: stats({ finance: 25, newbusiness: 25, jobs: 20, technology: 15, realestate: 15 }) },
  { id: 'history', name: 'History & heritage', line: 'How a place got to be the way it is.', stats: stats({ history: 40, arts: 20, civic: 15, tourism: 15, events: 10 }) },
  { id: 'pop', name: 'Pop culture', line: 'Celebrity, reality TV, music, true crime.', stats: stats({ celebritynews: 30, realitytv: 25, artistsbands: 20, truecrime: 15, arts: 10 }) },
  { id: 'world', name: 'World & politics', line: 'Politics, geopolitics, the markets, the military.', stats: stats({ politics: 25, geopolitics: 25, finance: 15, military: 15, technology: 10, history: 10 }) },
]
export const classOf = (id: string | null | undefined) => CLASSES.find((c) => c.id === id)
/** A business's starting class (BIZ_CLASS). */
export const BIZ_CLASS: Record<string, string> = { 'Real estate': 'realestate', Lending: 'money', Tourism: 'todo', 'A shop or restaurant': 'food', Other: 'local' }
/** Abroad the draw is travel, food and activities (v2.3); only the order changes. */
export const ABROAD_FIRST = ['todo', 'food', 'outdoors', 'history', 'sports', 'pop', 'money', 'realestate', 'world', 'local']

/** The class's topics, strongest first, as many as the plan allows (at most six). */
export function classTopics(cls: string, maxTopics: number): string[] {
  const c = classOf(cls)
  if (!c) return []
  return Object.entries(c.stats).sort((a, b) => b[1] - a[1]).slice(0, Math.min(maxTopics, 6)).map(([t]) => t)
}

export interface MixRow { t: string; v: number; lock: boolean }

/** The class's weights for the topics it has, an even share for the rest, all adding to 100 (mixFromClass). */
export function mixFromClass(cls: string | null, ids: string[]): MixRow[] {
  if (!ids.length) return []
  const c = classOf(cls)
  const w = ids.map((t) => (c && c.stats[t]) || 0)
  const miss = w.filter((x) => !x).length
  const have = w.reduce((a, b) => a + b, 0)
  const fill = miss ? Math.max(5, Math.round(have / Math.max(1, ids.length - miss))) : 0
  const raw = w.map((x) => x || fill)
  const sum = raw.reduce((a, b) => a + b, 0)
  const out = raw.map((x) => Math.floor((x / sum) * 100))
  let r = 100 - out.reduce((a, b) => a + b, 0)
  raw.map((x, i) => [(x / sum) * 100 - out[i], i] as const).sort((a, b) => b[0] - a[0]).forEach(([, i]) => { if (r > 0) { out[i]++; r-- } })
  return ids.map((t, i) => ({ t, v: out[i], lock: false }))
}

export function evenMix(ids: string[]): MixRow[] {
  const n = ids.length
  if (!n) return []
  const base = Math.floor(100 / n)
  const left = 100 - base * n
  return ids.map((t, i) => ({ t, v: base + (i < left ? 1 : 0), lock: false }))
}

/** The app's rule: the mix stays at 100; the unlocked others make room in proportion (setShare). */
export function setShare(M: MixRow[], i: number, v: number): MixRow[] {
  const next = M.map((r) => ({ ...r }))
  const lockSum = next.reduce((s, r, j) => s + (j !== i && r.lock ? r.v : 0), 0)
  const avail = 100 - lockSum
  v = Math.max(0, Math.min(avail, Math.round(v)))
  const others = next.map((_, j) => j).filter((j) => j !== i && !next[j].lock)
  if (!others.length) { next[i].v = avail; return next }
  const oSum = others.reduce((s, j) => s + next[j].v, 0)
  const rest = avail - v
  next[i].v = v
  const raw = others.map((j) => (oSum > 0 ? (next[j].v / oSum) * rest : rest / others.length))
  const fl = raw.map(Math.floor)
  let r = rest - fl.reduce((a, b) => a + b, 0)
  raw.map((x, k) => [x - fl[k], k] as const).sort((a, b) => b[0] - a[0]).forEach(([, k]) => { if (r > 0) { fl[k]++; r-- } })
  others.forEach((j, k) => { next[j].v = fl[k] })
  return next
}

/** Every topic a national or world subject: the place step asks where in the world (v2). */
export const allWide = (topics: string[]) => topics.length > 0 && topics.every(isWide)

// ---------------- The mix tour and the positions ----------------

export const CAPS = ['Your mix always adds up to 100.', 'Pull one up, and the others make room.', 'Lock one to hold it where it is.', 'Your turn. Or leave it: it’s in Feed controls › Topics any time.']

export type Position = 'insider' | 'reporter' | 'newcomer'
export const POSITIONS: { v: Position; said: string; name: string; what: string }[] = [
  { v: 'insider', said: 'People ask me about this', name: 'Insider', what: 'You speak with familiarity. The facts still come from the record.' },
  { v: 'reporter', said: 'I cover it because I should', name: 'Reporter', what: 'Plain and straight: what happened and what happens next.' },
  { v: 'newcomer', said: 'I’m curious, still learning it', name: 'Newcomer', what: 'An honest open question to your followers.' },
]
export const defaultPos = (kind: string): Position => (kind === 'business' ? 'reporter' : 'insider')

// ---------------- Done: a look at the first brief ----------------

export const SAMPLE: Record<string, string> = {
  events: 'This weekend’s lineup, and the one to get to early', 'food-drink': 'A new spot opens downtown, and the line already says it',
  'new-business-openings': 'What’s moving into the old storefront on Main', 'civic-government': 'What council voted on last night, in plain words',
  'outdoor-recreation': 'Trail conditions before the weekend', 'development-construction': 'The project the planning board sees next week',
  'real-estate-market': 'What homes sold for this month, and what that means', 'arts-culture': 'The show people will be talking about',
  schools: 'The school board’s calendar change', 'tourism-destination': 'Where visitors are going this month',
  'snow-weather': 'The storm line this week, and what to expect', finance: 'This week’s rate move, explained', 'jobs-hiring': 'Who’s hiring in town',
}

// ---------------- The steps ----------------

export const ORDER = ['greet', 'hello', 'name', 'kind', 'langs', 'cls', 'topics', 'place', 'connect', 'mix', 'positions', 'voice', 'build', 'done'] as const
export type Step = (typeof ORDER)[number]
/** The sections across the top (You, Topics, Place, Mix, Voice) and the screens in each. */
export const SECS: [string, Step[]][] = [
  ['You', ['name', 'kind', 'langs']], ['Topics', ['cls', 'topics']], ['Place', ['place', 'connect']], ['Mix', ['mix', 'positions']], ['Voice', ['voice']],
]
